import React from 'react';

import { Box } from '@mui/material';

import CalendarDesktopEventBar from './CalendarDesktopEventBar';
import CalendarDesktopEventTooltip from './CalendarDesktopEventTooltip';
import { CalendarDayProps, CalendarUserProps } from '../../@types/calendar/types';
import {
  CALENDAR_CELL_WIDTH,
  CALENDAR_CELL_HEIGHT,
  CALENDAR_CELL_BORDER_COLOR,
  CALENDAR_CELL_BORDER_STYLE,
  CALENDAR_CELL_BORDER_WIDTH,
  CALENDAR_HOLIDAY_CELL_BACKGROUND,
  CALENDAR_WEEKEND_CELL_BACKGROUND,
  CALENDAR_WORKDAY_CELL_BACKGROUND,
} from '../../@types/calendar/constants/cell';

// ----------------------------------------------------------------

type CalendarDesktopCellProps = {
  day: CalendarDayProps;
  user: CalendarUserProps;
};

const CalendarDesktopEventCell: React.FC<CalendarDesktopCellProps> = ({ day, user }) => {
  const { isHoliday, isWeekend, dateId, date } = day;
  const { events } = user;

  const dayEvents = events[dateId] ?? [];
  const concurrentCount = dayEvents[0]?.concurrentCount ?? 1;

  // Grid template by concurrentCount
  const gridTemplateRows = `repeat(${concurrentCount}, 1fr)`;
  const gridTemplateAreas = Array.from(
    { length: concurrentCount },
    (_, i) => `"concurrentIndex${i + 1}"`
  ).join(' ');

  const cellStyle = {
    width: `${CALENDAR_CELL_WIDTH}px`,
    maxWidth: `${CALENDAR_CELL_WIDTH}px`,
    minWidth: `${CALENDAR_CELL_WIDTH}px`,
    height: `${CALENDAR_CELL_HEIGHT}px`,
    backgroundColor: isHoliday
      ? CALENDAR_HOLIDAY_CELL_BACKGROUND
      : isWeekend
        ? CALENDAR_WEEKEND_CELL_BACKGROUND
        : CALENDAR_WORKDAY_CELL_BACKGROUND,
    borderColor: CALENDAR_CELL_BORDER_COLOR,
    borderWidth: `${CALENDAR_CELL_BORDER_WIDTH}px`,
    borderStyle: CALENDAR_CELL_BORDER_STYLE,
    display: 'grid',
    alignItems: 'center',
    justifyContent: 'center',
    gridTemplateColumns: '1fr',
    gridTemplateRows,
    gridTemplateAreas,
    gap: 0.8,
    py: 0.8,
  };

  return (
    <CalendarDesktopEventTooltip events={dayEvents} date={date} user={user}>
      <Box sx={{ ...cellStyle }}>
        {dayEvents.length > 0 &&
          dayEvents.map((dayEvent, index) => (
            <Box
              key={`dayEvent-${index}`}
              sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                justifyContent:
                  dayEvent.start && dayEvent.end
                    ? 'center'
                    : dayEvent.start
                      ? 'right'
                      : dayEvent.end
                        ? 'left'
                        : 'center',
                gridArea: `concurrentIndex${dayEvent.concurrentIndex}`,
                alignItems: 'center',
              }}
            >
              <Box
                sx={{
                  width:
                    dayEvent.start && dayEvent.end
                      ? `calc(100% / 2)`
                      : dayEvent.start || dayEvent.end
                        ? `calc(100% / 4 * 3)`
                        : '100%',
                  height: '100%',
                  maxHeight: `${CALENDAR_CELL_WIDTH / 2}px`,
                }}
              >
                <CalendarDesktopEventBar event={dayEvent} />
              </Box>
            </Box>
          ))}
      </Box>
    </CalendarDesktopEventTooltip>
  );
};

export default CalendarDesktopEventCell;
